import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box, Button, Typography, Alert, CircularProgress,
  TextField, IconButton, Paper, Chip, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemText, ListItemSecondaryAction,
  Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { saveCarteJson } from '../utils/api';

const navStyle = { display: 'flex', gap: 1, mb: 3, borderBottom: '1px solid #eee', pb: 2 };

const DashboardCategories = () => {
  const navigate = useNavigate();
  const [user] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('user')); } catch { return null; }
  });

  const [carte, setCarte] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Sélection de la catégorie active dans le panneau gauche
  const [selectedCat, setSelectedCat] = useState(null);

  // Dialogues
  const [catDialog, setCatDialog] = useState({ open: false, mode: 'add', value: '', original: '' });
  const [sousDialog, setSousDialog] = useState({ open: false, mode: 'add', value: '', original: '' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: '', target: '' });

  // ── Chargement ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user) { navigate('/dashboard'); return; }
    fetch(`/assets/json/carte.json?v=${Date.now()}`)
      .then(r => r.json())
      .then(data => {
        setCarte(data);
        const firstCat = Object.keys(data.categories)[0];
        setSelectedCat(firstCat || null);
      })
      .catch(() => setMessage({ type: 'error', text: 'Erreur chargement carte.json' }));
  }, [user, navigate]);

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const getSubCats = (cat) => {
    if (!carte || !cat) return [];
    const items = carte.categories[cat] || [];
    return [...new Set(items.map(i => i['sous-categ']).filter(Boolean))];
  };

  const getCategoryItemCount = (cat) =>
    carte ? (carte.categories[cat] || []).length : 0;

  // ── Sauvegarde ───────────────────────────────────────────────────────────────
  const handleSave = async (newCarte) => {
    setSaving(true);
    setMessage(null);
    try {
      await saveCarteJson(newCarte, user);
      setMessage({ type: 'success', text: 'Sauvegardé avec succès !' });
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'Erreur lors de la sauvegarde' });
    }
    setSaving(false);
  };

  // ══ Catégories principales ════════════════════════════════════════════════════

  const openAddCat = () => setCatDialog({ open: true, mode: 'add', value: '', original: '' });
  const openRenameCat = (cat) => setCatDialog({ open: true, mode: 'rename', value: cat, original: cat });

  const handleCatDialogConfirm = () => {
    const name = catDialog.value.trim().toLowerCase();
    if (!name) return;

    setCarte(prev => {
      const cats = { ...prev.categories };
      if (catDialog.mode === 'add') {
        if (cats[name]) return prev; // déjà existant
        cats[name] = [];
      } else {
        // rename : recréer dans le bon ordre
        const entries = Object.entries(cats);
        const newEntries = entries.map(([k, v]) =>
          k === catDialog.original ? [name, v] : [k, v]
        );
        const rebuilt = Object.fromEntries(newEntries);
        const updated = { ...prev, categories: rebuilt };
        setCatDialog({ open: false, mode: 'add', value: '', original: '' });
        if (selectedCat === catDialog.original) setSelectedCat(name);
        return updated;
      }
      return { ...prev, categories: cats };
    });
    setCatDialog({ open: false, mode: 'add', value: '', original: '' });
  };

  const openDeleteCat = (cat) => setDeleteDialog({ open: true, type: 'cat', target: cat });

  const handleDeleteCat = () => {
    const cat = deleteDialog.target;
    setCarte(prev => {
      const cats = { ...prev.categories };
      delete cats[cat];
      const remaining = Object.keys(cats);
      if (selectedCat === cat) setSelectedCat(remaining[0] || null);
      return { ...prev, categories: cats };
    });
    setDeleteDialog({ open: false, type: '', target: '' });
  };

  // ══ Sous-catégories ════════════════════════════════════════════════════════════

  const openAddSous = () => setSousDialog({ open: true, mode: 'add', value: '', original: '' });
  const openRenameSous = (s) => setSousDialog({ open: true, mode: 'rename', value: s, original: s });

  const handleSousDialogConfirm = () => {
    const name = sousDialog.value.trim();
    if (!name || !selectedCat) return;

    setCarte(prev => {
      const cats = { ...prev.categories };
      const items = [...(cats[selectedCat] || [])];

      if (sousDialog.mode === 'rename') {
        // Met à jour tous les items qui avaient l'ancienne valeur
        const updated = items.map(item =>
          item['sous-categ'] === sousDialog.original
            ? { ...item, 'sous-categ': name }
            : item
        );
        cats[selectedCat] = updated;
      }
      // mode 'add' : la sous-catégorie n'existe qu'une fois qu'un item la référence
      // On ne fait rien ici, elle sera utilisée depuis le dashboard Plats.
      // Mais pour la visibilité, on peut créer un item vide avec cette sous-categ.

      return { ...prev, categories: cats };
    });
    setSousDialog({ open: false, mode: 'add', value: '', original: '' });
  };

  const openDeleteSous = (s) => setDeleteDialog({ open: true, type: 'sous', target: s });

  const handleDeleteSous = () => {
    const sousToDelete = deleteDialog.target;
    setCarte(prev => {
      const cats = { ...prev.categories };
      const items = (cats[selectedCat] || []).map(item =>
        item['sous-categ'] === sousToDelete
          ? { ...item, 'sous-categ': '' }
          : item
      );
      cats[selectedCat] = items;
      return { ...prev, categories: cats };
    });
    setDeleteDialog({ open: false, type: '', target: '' });
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  if (!user) return null;
  if (!carte) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  const categories = Object.keys(carte.categories);
  const subCats = getSubCats(selectedCat);

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      {/* Nav */}
      <Box sx={navStyle}>
        <Button component={Link} to="/dashboard" variant="outlined" size="small">Commandes</Button>
        <Button component={Link} to="/dashboard/plats" variant="outlined" size="small">Plats</Button>
        <Button component={Link} to="/dashboard/menus" variant="outlined" size="small">Menus</Button>
        <Button variant="contained" size="small" disabled>Catégories</Button>
        <Button component={Link} to="/dashboard/reglages" variant="outlined" size="small">Fermeture / Vacances</Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" color="error" onClick={() => { sessionStorage.removeItem('user'); navigate('/dashboard'); }}>
          Déconnexion
        </Button>
      </Box>

      <Typography variant="h4" gutterBottom>Gestion des catégories</Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>

        {/* ── Panneau gauche : catégories ────────────────────────────── */}
        <Paper variant="outlined" sx={{ width: 280, flexShrink: 0 }}>
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle1" fontWeight={600}>Catégories</Typography>
            <Tooltip title="Ajouter une catégorie">
              <IconButton size="small" color="primary" onClick={openAddCat}>
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Divider />
          <List dense disablePadding>
            {categories.map((cat) => (
              <ListItem
                key={cat}
                button
                selected={selectedCat === cat}
                onClick={() => setSelectedCat(cat)}
                sx={{
                  px: 2,
                  '&.Mui-selected': { bgcolor: 'primary.light', '&:hover': { bgcolor: 'primary.light' } }
                }}
              >
                <DragIndicatorIcon sx={{ color: '#ccc', mr: 1, fontSize: 18 }} />
                <ListItemText
                  primary={<span style={{ textTransform: 'capitalize', fontWeight: selectedCat === cat ? 600 : 400 }}>{cat}</span>}
                  secondary={`${getCategoryItemCount(cat)} plat(s)`}
                />
                <ListItemSecondaryAction>
                  <Tooltip title="Renommer">
                    <IconButton edge="end" size="small" onClick={(e) => { e.stopPropagation(); openRenameCat(cat); }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Supprimer">
                    <IconButton edge="end" size="small" color="error" onClick={(e) => { e.stopPropagation(); openDeleteCat(cat); }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Paper>

        {/* ── Panneau droit : sous-catégories ────────────────────────── */}
        <Paper variant="outlined" sx={{ flex: 1 }}>
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Sous-catégories
              {selectedCat && (
                <Chip label={selectedCat} size="small" sx={{ ml: 1, textTransform: 'capitalize' }} />
              )}
            </Typography>
            {selectedCat && (
              <Tooltip title="Ajouter une sous-catégorie">
                <IconButton size="small" color="primary" onClick={openAddSous}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Divider />

          {!selectedCat ? (
            <Box sx={{ p: 3, color: 'text.secondary' }}>
              <Typography variant="body2">Sélectionnez une catégorie à gauche.</Typography>
            </Box>
          ) : subCats.length === 0 ? (
            <Box sx={{ p: 3, color: 'text.secondary' }}>
              <Typography variant="body2">Aucune sous-catégorie. Ajoutez-en une ou créez des plats depuis le dashboard Plats.</Typography>
            </Box>
          ) : (
            <List dense disablePadding>
              {subCats.map((s) => {
                const count = (carte.categories[selectedCat] || []).filter(i => i['sous-categ'] === s).length;
                return (
                  <ListItem key={s} divider sx={{ px: 2 }}>
                    <ListItemText
                      primary={s}
                      secondary={`${count} plat(s)`}
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Renommer (met à jour tous les plats)">
                        <IconButton edge="end" size="small" onClick={() => openRenameSous(s)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Vider la sous-catégorie (les plats restent)">
                        <IconButton edge="end" size="small" color="error" onClick={() => openDeleteSous(s)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          )}
        </Paper>
      </Box>

      {/* Bouton sauvegarde */}
      <Box sx={{ mt: 3 }}>
        <Button variant="contained" onClick={() => handleSave(carte)} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Sauvegarder les modifications'}
        </Button>
      </Box>

      {/* ── Dialogue catégorie ──────────────────────────────────────────── */}
      <Dialog open={catDialog.open} onClose={() => setCatDialog(p => ({ ...p, open: false }))} maxWidth="xs" fullWidth>
        <DialogTitle>{catDialog.mode === 'add' ? 'Ajouter une catégorie' : 'Renommer la catégorie'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Nom de la catégorie"
            value={catDialog.value}
            onChange={e => setCatDialog(p => ({ ...p, value: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && handleCatDialogConfirm()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCatDialog(p => ({ ...p, open: false }))}>Annuler</Button>
          <Button variant="contained" onClick={handleCatDialogConfirm}>
            {catDialog.mode === 'add' ? 'Ajouter' : 'Renommer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialogue sous-catégorie ─────────────────────────────────────── */}
      <Dialog open={sousDialog.open} onClose={() => setSousDialog(p => ({ ...p, open: false }))} maxWidth="xs" fullWidth>
        <DialogTitle>{sousDialog.mode === 'add' ? 'Ajouter une sous-catégorie' : 'Renommer la sous-catégorie'}</DialogTitle>
        <DialogContent>
          {sousDialog.mode === 'add' && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              La sous-catégorie sera disponible à la sélection dans le dashboard Plats.
            </Typography>
          )}
          {sousDialog.mode === 'rename' && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Tous les plats avec la sous-catégorie <strong>"{sousDialog.original}"</strong> seront mis à jour.
            </Typography>
          )}
          <TextField
            autoFocus
            fullWidth
            label="Nom de la sous-catégorie"
            value={sousDialog.value}
            onChange={e => setSousDialog(p => ({ ...p, value: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && handleSousDialogConfirm()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSousDialog(p => ({ ...p, open: false }))}>Annuler</Button>
          <Button variant="contained" onClick={handleSousDialogConfirm}>
            {sousDialog.mode === 'add' ? 'Ajouter' : 'Renommer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialogue confirmation suppression ──────────────────────────── */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, type: '', target: '' })} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          {deleteDialog.type === 'cat' ? (
            <Typography>
              Supprimer la catégorie <strong>"{deleteDialog.target}"</strong> ?<br />
              <Typography component="span" variant="body2" color="error">
                Attention : tous les plats de cette catégorie ({getCategoryItemCount(deleteDialog.target)} plat(s)) seront également supprimés.
              </Typography>
            </Typography>
          ) : (
            <Typography>
              Vider la sous-catégorie <strong>"{deleteDialog.target}"</strong> ?<br />
              <Typography component="span" variant="body2" color="text.secondary">
                Les plats conserveront leurs données mais leur sous-catégorie sera effacée.
              </Typography>
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, type: '', target: '' })}>Annuler</Button>
          <Button
            variant="contained"
            color="error"
            onClick={deleteDialog.type === 'cat' ? handleDeleteCat : handleDeleteSous}
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DashboardCategories;
